import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuditTimeZone } from "../../hooks/useAuditTimeZone";
import { DriverLabel } from "../../hooks/useDriverLabels";
import { AuditRow } from "../../models/AuditRow";
import { AuditTrip } from "../../models/AuditTrip";
import { AuditView } from "../../models/AuditView";
import { formatConsumption, formatDistance, formatDuration, formatNumber } from "../../utils/format";
import { formatClockInZone, formatDayInZone } from "../../utils/timeZone";
import { ConsumptionCell } from "./cells/ConsumptionCell";
import { DriverCell } from "./cells/DriverCell";
import { DutyRosterCell } from "./cells/DutyRosterCell";
import { MapButtonCell } from "./cells/MapButtonCell";
import { ReasonChipsCell } from "./cells/ReasonChipsCell";
import { RefuelCell } from "./cells/RefuelCell";
import {
  AUDIT_FIELDS,
  rowAdBlueLiters,
  rowConsumption,
  rowDate,
  rowDistance,
  rowDriverIds,
  rowDurationMs,
  rowDutyRosters,
  rowFirstStart,
  rowLastStop,
  rowOdometerEndKm,
  rowOdometerStartKm,
  rowReasonText,
  rowRefuelLiters,
  rowStartTrip,
  rowStopTrip,
} from "./rowValues";
import { DistanceDecimals } from "@/constants/distanceDecimals";
import { FUEL_TYPE_UNITS } from "@/constants/FuelTypeGroups";

export interface AuditColumnContext {
  view: AuditView;
  distanceDecimals: DistanceDecimals;
  driverLabel: DriverLabel;
  getStartAddress: (trip: AuditTrip) => string;
  getStopAddress: (trip: AuditTrip) => string;
  onShowMap: (row: AuditRow) => void;
  onShowReasonMap: (row: AuditRow, reasonLabel: string) => void;
}

/**
 * Column definitions shared by the grid and the mobile cards. The digital view lists Geotab's
 * figures in the order of the customer's digital Fahrtenbuch (vehicle, date, driver, start,
 * odometer start, duration, distance, end, odometer end, energy product, consumption, fuel-ups,
 * reason, map). The analog view lists only what the driver entered in the EcoApp: date, driver,
 * duty roster, start and end of the booking, the driver's odometer readings and distance, the
 * fuel-ups typed into the app and the reason.
 */
export function useAuditColumns(context: AuditColumnContext): GridColDef<AuditRow>[] {
  const { t, i18n } = useTranslation();
  const { view, distanceDecimals, driverLabel, getStartAddress, getStopAddress, onShowMap, onShowReasonMap } = context;
  const language = i18n.language;
  const timeZone = useAuditTimeZone();

  return useMemo(() => {
    const analog = view === "analog";
    const numeric = { align: "right", headerAlign: "right", type: "number" } as const;
    const kilometres = (value: number | null) => (value == null ? "" : `${formatNumber(value, language, analog ? 0 : 1)} km`);
    const litres = (value: number | null) => (value == null ? "" : `${formatNumber(value, language, 1)} l`);
    // Fuel of an electric vehicle is what Geotab's charging detection reports, in kWh.
    const refuelled = (value: number | null, row: AuditRow) =>
      value == null ? "" : `${formatNumber(value, language, 1)} ${row.device.fuelType === "electric" ? FUEL_TYPE_UNITS.electric : "l"}`;
    const clock = (value: Date | null) => (value ? formatClockInZone(value, timeZone) : "");

    const licensePlate: GridColDef<AuditRow> = {
      field: AUDIT_FIELDS.licensePlate,
      headerName: t("logbookAudit.columns.licensePlate"),
      minWidth: 130,
      valueGetter: (_value, row) => row.device.licensePlate,
    };
    const vin: GridColDef<AuditRow> = {
      field: AUDIT_FIELDS.vin,
      headerName: t("logbookAudit.columns.vin"),
      minWidth: 170,
      valueGetter: (_value, row) => row.device.vin,
    };
    const date: GridColDef<AuditRow> = {
      field: AUDIT_FIELDS.date,
      headerName: t("logbookAudit.columns.date"),
      type: "date",
      minWidth: 110,
      valueGetter: (_value, row) => rowDate(row),
      valueFormatter: (value: Date) => (value ? formatDayInZone(value, timeZone) : ""),
    };
    const driver: GridColDef<AuditRow> = {
      field: AUDIT_FIELDS.driver,
      headerName: t("logbookAudit.columns.driver"),
      description: t(analog ? "logbookAudit.source.appDriver" : "logbookAudit.source.geotabDriver"),
      minWidth: 160,
      valueGetter: (_value, row) => rowDriverIds(row).map(driverLabel).join(", "),
      renderCell: (params: GridRenderCellParams<AuditRow>) => <DriverCell {...params} driverLabel={driverLabel} />,
    };
    const firstStart: GridColDef<AuditRow> = {
      field: AUDIT_FIELDS.firstStart,
      headerName: t("logbookAudit.columns.firstStart"),
      description: t(analog ? "logbookAudit.source.appStart" : "logbookAudit.source.geotabTrip"),
      type: "dateTime",
      minWidth: 120,
      valueGetter: (_value, row) => rowFirstStart(row),
      valueFormatter: clock,
    };
    const lastStop: GridColDef<AuditRow> = {
      field: AUDIT_FIELDS.lastStop,
      headerName: t("logbookAudit.columns.lastStop"),
      description: t(analog ? "logbookAudit.source.appStop" : "logbookAudit.source.geotabTrip"),
      type: "dateTime",
      minWidth: 120,
      valueGetter: (_value, row) => rowLastStop(row) ?? null,
      valueFormatter: clock,
    };
    const odometerStart: GridColDef<AuditRow> = {
      field: AUDIT_FIELDS.odometerStart,
      headerName: t("logbookAudit.columns.odometerStart"),
      description: t(analog ? "logbookAudit.source.appOdometer" : "logbookAudit.source.geotabOdometer"),
      ...numeric,
      minWidth: 130,
      valueGetter: (_value, row) => rowOdometerStartKm(row) ?? null,
      valueFormatter: kilometres,
    };
    const odometerEnd: GridColDef<AuditRow> = {
      field: AUDIT_FIELDS.odometerEnd,
      headerName: t("logbookAudit.columns.odometerEnd"),
      description: t(analog ? "logbookAudit.source.appOdometerEnd" : "logbookAudit.source.geotabOdometer"),
      ...numeric,
      minWidth: 130,
      valueGetter: (_value, row) => rowOdometerEndKm(row) ?? null,
      valueFormatter: kilometres,
    };
    const duration: GridColDef<AuditRow> = {
      field: AUDIT_FIELDS.duration,
      headerName: t("logbookAudit.columns.duration"),
      description: t(analog ? "logbookAudit.source.analogDuration" : "logbookAudit.source.geotabTrip"),
      ...numeric,
      minWidth: 110,
      valueGetter: (_value, row) => rowDurationMs(row) ?? null,
      valueFormatter: (value: number | null) => (value == null ? "" : formatDuration(value)),
    };
    const distance: GridColDef<AuditRow> = {
      field: AUDIT_FIELDS.distance,
      headerName: t("logbookAudit.columns.distance"),
      description: t(analog ? "logbookAudit.source.driverDistance" : "logbookAudit.source.odometerDistance"),
      ...numeric,
      minWidth: 110,
      valueGetter: (_value, row) => rowDistance(row) ?? null,
      valueFormatter: (value: number | null) => (value == null ? "" : formatDistance(value, language, analog ? 0 : distanceDecimals)),
    };
    const refuel: GridColDef<AuditRow> = {
      field: AUDIT_FIELDS.refuel,
      headerName: t("logbookAudit.columns.refuel"),
      description: t(analog ? "logbookAudit.source.appFuelTransactions" : "logbookAudit.source.fuelTransactions"),
      ...numeric,
      minWidth: 140,
      valueGetter: (_value, row) => rowRefuelLiters(row) ?? null,
      valueFormatter: refuelled,
      renderCell: (params: GridRenderCellParams<AuditRow>) => <RefuelCell {...params} kind="fuel" />,
    };
    const adBlue: GridColDef<AuditRow> = {
      field: AUDIT_FIELDS.adBlue,
      headerName: t("logbookAudit.columns.adBlue"),
      description: t(analog ? "logbookAudit.source.appFuelTransactions" : "logbookAudit.source.adBlueTransactions"),
      ...numeric,
      minWidth: 110,
      valueGetter: (_value, row) => rowAdBlueLiters(row) ?? null,
      valueFormatter: litres,
      renderCell: (params: GridRenderCellParams<AuditRow>) => <RefuelCell {...params} kind="adBlue" />,
    };
    const reason: GridColDef<AuditRow> = {
      field: AUDIT_FIELDS.reason,
      headerName: t("logbookAudit.columns.reason"),
      description: t("logbookAudit.source.appReason"),
      minWidth: 220,
      flex: 1,
      valueGetter: (_value, row) => rowReasonText(row),
      renderCell: (params: GridRenderCellParams<AuditRow>) => (
        <ReasonChipsCell {...params} onShowReasonMap={analog ? undefined : onShowReasonMap} />
      ),
    };

    if (analog) {
      const dutyRoster: GridColDef<AuditRow> = {
        field: AUDIT_FIELDS.dutyRoster,
        headerName: t("logbookAudit.columns.dutyRoster"),
        description: t("logbookAudit.source.appDutyRoster"),
        minWidth: 160,
        valueGetter: (_value, row) => rowDutyRosters(row).join(", "),
        renderCell: DutyRosterCell,
      };
      return [licensePlate, vin, date, driver, dutyRoster, firstStart, odometerStart, duration, distance, lastStop, odometerEnd, refuel, adBlue, reason];
    }

    const startAddress: GridColDef<AuditRow> = {
      field: AUDIT_FIELDS.startAddress,
      headerName: t("logbookAudit.columns.startAddress"),
      minWidth: 260,
      valueGetter: (_value, row) => getStartAddress(rowStartTrip(row)),
    };
    const stopAddress: GridColDef<AuditRow> = {
      field: AUDIT_FIELDS.stopAddress,
      headerName: t("logbookAudit.columns.stopAddress"),
      minWidth: 260,
      valueGetter: (_value, row) => getStopAddress(rowStopTrip(row)),
    };
    const fuelType: GridColDef<AuditRow> = {
      field: AUDIT_FIELDS.fuelType,
      headerName: t("logbookAudit.columns.fuelType"),
      description: t("logbookAudit.source.geotabGroup"),
      minWidth: 130,
      valueGetter: (_value, row) => (row.device.fuelType ? t(`logbookAudit.fuelType.${row.device.fuelType}`) : ""),
    };
    const consumption: GridColDef<AuditRow> = {
      field: AUDIT_FIELDS.consumption,
      headerName: t("logbookAudit.columns.consumption"),
      description: `${t("logbookAudit.source.geotabConsumption")} ${t("logbookAudit.consumption.cngHint")}`,
      ...numeric,
      minWidth: 130,
      valueGetter: (_value, row) => rowConsumption(row).value ?? null,
      valueFormatter: (value: number | null, row) => (value == null ? "" : formatConsumption(value, row.device.fuelType, language)),
      renderCell: ConsumptionCell,
    };
    const map: GridColDef<AuditRow> = {
      field: AUDIT_FIELDS.map,
      headerName: t("logbookAudit.columns.map"),
      width: 70,
      sortable: false,
      filterable: false,
      disableExport: true,
      disableColumnMenu: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<AuditRow>) => <MapButtonCell {...params} onShowMap={onShowMap} />,
    };
    return [licensePlate, vin, date, driver, firstStart, startAddress, odometerStart, duration, distance, lastStop, stopAddress, odometerEnd, fuelType, consumption, refuel, adBlue, reason, map];
  }, [t, language, timeZone, view, distanceDecimals, driverLabel, getStartAddress, getStopAddress, onShowMap, onShowReasonMap]);
}

/**
 * Columns hidden by default per view (the customer's default layout of 26.09.2026: vehicle,
 * date, odometer, distance, consumption, fuel-ups and reason; times, driver, plate and VIN on
 * request). The user's own choice is persisted on top.
 */
export function defaultHiddenFields(view: AuditView): string[] {
  const common = [
    AUDIT_FIELDS.licensePlate,
    AUDIT_FIELDS.vin,
    AUDIT_FIELDS.driver,
    AUDIT_FIELDS.firstStart,
    AUDIT_FIELDS.duration,
    AUDIT_FIELDS.lastStop,
  ];
  return view === "analog" ? common : [...common, AUDIT_FIELDS.startAddress, AUDIT_FIELDS.stopAddress];
}
